import { Alert } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { decode } from 'html-entities';
import { DateTime } from 'luxon';
import { Waypoint } from 'react-waypoint';

import { useAppSelector } from 'hooks';
import { Avatar } from 'components/UI';
import { ImageIcon, AttachedIcon, LinkIcon } from 'assets/icons';
import { getPath } from 'routes';
import { PatientTabs } from 'constants/enums';
import { Loading } from 'components/Fetch';
import AvatarNameColor from 'components/UI/AvatarNameColor';
import { ChatThreadParticipantType } from 'utils/Chat';
import { getAllAdmins } from 'store/groups/reducer';

interface IMessagingPanelFiltersProps {
  messages: ((IMessages | IMessageV2) & { version: number })[];
  handleGetNotificationsWithDelay: any;
  setShow: any;
  loadMoreContent: any;
  getMessageLoading: any;
  user: IUser | null;
}

const MessagingPanelMessages = ({
  messages,
  handleGetNotificationsWithDelay,
  setShow,
  loadMoreContent,
  getMessageLoading,
  user
}: IMessagingPanelFiltersProps) => {
  const intl = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);
  const admins = useAppSelector(getAllAdmins);

  return (
    <div className="d-flex flex-column p-0 m-0" id="messagingPanelMessagesRoot">
      {!getMessageLoading && !messages?.length && (
        <Alert variant="primary" className="m-2">
          {intl.formatMessage({ id: 'noMessagesKey' })}
        </Alert>
      )}
      {messages &&
        messages.map((message, index) => {
          if (message.version === 2) {
            const messageV2: IMessageV2 = message as any;
            // "message" in v2 is a thread that also has info like
            // most recent message attached to it
            const currentUserIsSender = messageV2.lastMessageSenderUid === user?.aid;
            const senderAdmin = admins.find(
              (admin) => admin.aid === messageV2.lastMessageSenderUid
            );
            const patientPageUrl = getPath(true).dashboard.patient(
              messageV2.uid,
              PatientTabs.MESSAGING
            );
            const patientUid = messageV2.uid;
            const patientDisplayName =
              messageV2.participants?.find((chatUser) => chatUser.uid === patientUid)?.azureUser
                ?.displayName || '';
            const unReadByClinician = !messageV2.participants.some(
              (participant) =>
                participant.type === ChatThreadParticipantType.CLINICIAN &&
                participant.lastMessageRead === 1
            );

            return (
              <div key={index}>
                <Link
                  className="messagingPanelMessageContainer p-3 pt-4 pb-4"
                  to={patientPageUrl}
                  onClick={() => [handleGetNotificationsWithDelay(), setShow(false)]}
                  data-test={`messaging-panel-${messageV2.uid}`}
                >
                  <Avatar size={40} />
                  <div className="d-flex flex-column ps-2 min-w-90p w-100">
                    <div className="d-inline-flex align-items-center justify-content-between">
                      <span
                        className={`${
                          unReadByClinician ? 'font-weight-bold' : 'font-weight-normal'
                        } text-dark`}
                      >
                        {`${patientDisplayName}`}
                      </span>
                      <div className="d-inline-flex align-items-center">
                        {unReadByClinician ? (
                          <div className="messagingPanelMessageEllipse" />
                        ) : (
                          messageV2.participants
                            .filter(
                              (participant) =>
                                participant.uid !== patientUid &&
                                participant.azureUser?.displayName &&
                                participant.lastMessageRead === 1 &&
                                participant.status === 1
                            )
                            .map((participant: any) => {
                              const readerAdmin = admins.find(
                                (admin) => admin.aid === participant.uid
                              );
                              return (
                                <AvatarNameColor
                                  key={readerAdmin?.aid || index}
                                  name={readerAdmin?.name || 'CareSimple User'}
                                  size={12}
                                  className="ms-2"
                                  overlay
                                />
                              );
                            })
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-between">
                      <div
                        className={`${
                          unReadByClinician ? 'font-weight-normal' : 'text-dark font-weight-light'
                        } text-truncate overflow-hidden`}
                      >
                        {currentUserIsSender ? (
                          <span className="me-1">{intl.formatMessage({ id: 'youKey' })}:</span>
                        ) : (
                          senderAdmin && <span className="me-1">{senderAdmin.name}:</span>
                        )}
                        <p className="m-0" data-test={`global-message-text-${messageV2.uid}`}>
                          {decode(messageV2.lastMessage)}
                        </p>
                      </div>
                      <span className="text-dark font-weight-light min-w-fit-content">
                        &bull;{' '}
                        {DateTime.fromISO(messageV2.lastMessageTimestamp)
                          .setLocale(locale)
                          .toLocaleString(DateTime.DATETIME_SHORT)}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            );
          }

          const messageV1: IMessages = message as any;
          const userSender = messageV1.sender === user?.aid;
          const patientPageUrl = getPath(true).dashboard.patient(
            messageV1.patient.uid,
            PatientTabs.MESSAGING
          );
          const unReadByClinician =
            messageV1.is_read === '0' && messageV1.sender === messageV1.about_aid;
          const isPatientSender: boolean = messageV1.sender === messageV1.about_aid;
          const senderAdmin = !isPatientSender
            ? admins.find((admin) => admin.aid === messageV1.sender)
            : undefined;
          return (
            <div key={messageV1.message_id}>
              <Link
                className="messagingPanelMessageContainer p-3 pt-4 pb-4"
                to={patientPageUrl}
                onClick={() => [handleGetNotificationsWithDelay(), setShow(false)]}
                data-test={`messaging-panel-${messageV1.patient.uid}`}
              >
                <Avatar size={40} imgUrl={messageV1.patient.avatar} />
                <div className="d-flex flex-column ps-2 min-w-90p w-100">
                  <div className="d-inline-flex align-items-center justify-content-between">
                    <span
                      className={`${
                        unReadByClinician ? 'font-weight-bold' : 'font-weight-normal'
                      } text-dark`}
                    >
                      {`${messageV1.patient.lastName}, ${messageV1.patient.firstName}`}
                    </span>
                    <div className="d-inline-flex align-items-center">
                      {unReadByClinician ? (
                        <div className="messagingPanelMessageEllipse" />
                      ) : (
                        messageV1.read_by.map((id: string) => {
                          const isPatientReader: boolean = id === messageV1.about_aid;
                          const readerAdmin = !isPatientReader
                            ? admins.find((admin) => admin.aid === id)
                            : undefined;
                          if (isPatientReader) {
                            return (
                              <Avatar
                                key={messageV1.patient.uid}
                                size={12}
                                className="ms-2"
                                imgUrl={messageV1.patient.avatar}
                                overlay={`${messageV1.patient.lastName}, ${messageV1.patient.firstName}`}
                              />
                            );
                          } else {
                            return (
                              <AvatarNameColor
                                key={readerAdmin?.aid || index}
                                name={readerAdmin?.name || 'CareSimple User'}
                                size={12}
                                className="ms-2"
                                overlay
                              />
                            );
                          }
                        })
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between">
                    <div
                      className={`${
                        unReadByClinician ? 'font-weight-normal' : 'text-dark font-weight-light'
                      } text-truncate overflow-hidden`}
                    >
                      {userSender ? (
                        <span className="me-1">{intl.formatMessage({ id: 'youKey' })}:</span>
                      ) : (
                        senderAdmin && <span className="me-1">{senderAdmin.name}:</span>
                      )}
                      {messageV1.message_data.text &&
                        !messageV1.message_data.image &&
                        !messageV1.message_data.media &&
                        !messageV1.message_data.surveyId && (
                          <p
                            className="m-0"
                            data-test={`global-message-text-${messageV1.about_uid}`}
                          >
                            {decode(messageV1.message_data.text)}
                          </p>
                        )}
                      {messageV1.message_data.image && !messageV1.message_data.url && (
                        <>
                          <ImageIcon className="icon" style={{ fontSize: '16px' }} />
                          <span
                            className="font-italic ms-1"
                            data-test={`global-message-image-${messageV1.about_uid}`}
                          >
                            {intl.formatMessage({ id: 'imageKey' })}
                          </span>
                        </>
                      )}
                      {messageV1.message_data.media && (
                        <>
                          <AttachedIcon className="icon" style={{ fontSize: '16px' }} />
                          <span
                            className="font-italic ms-1"
                            data-test={`global-message-pdf-${messageV1.about_uid}`}
                          >
                            {intl.formatMessage({ id: 'pdfKey' })}
                          </span>
                        </>
                      )}
                      {messageV1.message_data.url && (
                        <>
                          <LinkIcon className="icon" style={{ fontSize: '16px' }} />
                          <span
                            className="font-italic ms-1"
                            data-test={`global-message-url-${messageV1.about_uid}`}
                          >
                            {messageV1.message_data.url}
                          </span>
                        </>
                      )}
                    </div>
                    <span className="text-dark font-weight-light min-w-fit-content">
                      &bull;{' '}
                      {DateTime.fromMillis(Number(messageV1.message_date))
                        .setLocale(locale)
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </span>
                  </div>
                </div>
              </Link>
              {messages.length !== index + 1 && <hr className="m-0" />}
            </div>
          );
        })}
      <Waypoint onEnter={loadMoreContent} topOffset="-20%" />
      {getMessageLoading && <Loading className="m-3" />}
    </div>
  );
};

export default MessagingPanelMessages;
